import React from 'react';
import { CheckCircle, Info } from '@phosphor-icons/react';
import {
  totalFoundationBoxQty,
  cardQtyBadge,
  flavourCounterTone,
  customerUnitPrice,
  priceCalcDescription,
  sortFlavours,
} from '../lib/bundleHelpers';

// Per-product noun: Essentials products are labelled "variant(s)" instead of "flavour(s)"
// since the items in that range aren't strictly flavours (e.g. capsules, electrolytes).
const variantNoun = (product) => (product?.range === 'Essentials'
  ? { singular: 'variant', plural: 'variants', label: 'Variants' }
  : { singular: 'flavour', plural: 'flavours', label: 'Flavours' });

// One product card — the only "complex" piece is its conditional UI surface.
// Each render mode (Foundation Box / Foundation Single / Supporting +/- / Supporting checkboxes)
// has been extracted into a sub-component below to keep this file readable.

// Per-tier rule tooltips — supporting ratio: 1 per 1 kg Core, 2 per 2 kg Core.
const foundationRules = (boxSize) => {
  const isSmall = boxSize === 8;
  const support = isSmall ? 1 : 2;
  const items = support === 1 ? 'supporting item' : 'supporting items';
  const base = isSmall
    ? `Then add ${support} ${items} per Core tub — duplicates welcome.`
    : `Then add ${support} ${items} per Core tub — any products, max 1 of each flavour per tub.`;
  return {
    single: base,
    box: base,
    pallet: `Then add ${support} ${items} per Core tub — any supporting ranges you like.`,
  };
};

const FoundationModeToggles = ({ product, boxSize, singleMode, boxMode, palletMode, onToggleSingleMode, onToggleBoxMode, onTogglePalletMode, showSingleTier = true }) => {
  const rules = foundationRules(boxSize);
  // Single Tub is per-customer opt-in. Still render the row when it's already
  // active (e.g. a loaded legacy single-tier saver) so the user can untick it.
  const renderSingle = showSingleTier || !!singleMode;
  return (
    <div className="mt-3 pt-3 border-t border-border-color space-y-3" onClick={(e) => e.stopPropagation()}>
      {renderSingle && (
      <div>
        <label className="flex items-center gap-2 text-xs cursor-pointer text-text-primary">
          <input
            type="checkbox"
            checked={!!singleMode}
            onChange={() => onToggleSingleMode(product)}
            data-testid={`single-mode-toggle-${product.id}`}
            className="w-4 h-4 accent-yellow-500"
          />
          <span>Single Tub</span>
          <span className="text-text-secondary">— 1 to {boxSize === 8 ? 7 : 5} tubs</span>
        </label>
        {rules.single && (
          <p
            className="mt-1 ml-6 text-[11px] leading-snug text-text-secondary italic"
            data-testid={`single-rule-${product.id}`}
          >
            {rules.single}
          </p>
        )}
      </div>
      )}
      <div>
        <label className="flex items-center gap-2 text-xs cursor-pointer text-text-primary">
          <input
            type="checkbox"
            checked={!!boxMode}
            onChange={() => onToggleBoxMode(product)}
            data-testid={`box-mode-toggle-${product.id}`}
            className="w-4 h-4 accent-yellow-500"
          />
          <span>Box of {boxSize}</span>
          <span className="text-text-secondary">— {boxSize} to {5 * boxSize - 1} tubs (1+ boxes, partials welcome)</span>
        </label>
        {rules.box && (
          <p
            className="mt-1 ml-6 text-[11px] leading-snug text-text-secondary italic"
            data-testid={`box-rule-${product.id}`}
          >
            {rules.box}
          </p>
        )}
      </div>
      <div>
        <label className="flex items-center gap-2 text-xs cursor-pointer text-text-primary">
          <input
            type="checkbox"
            checked={!!palletMode}
            onChange={() => onTogglePalletMode && onTogglePalletMode(product)}
            data-testid={`pallet-mode-toggle-${product.id}`}
            className="w-4 h-4 accent-yellow-500"
          />
          <span>Pallet Layer</span>
          <span className="text-text-secondary">— 5+ boxes (free supporting choice)</span>
        </label>
        {rules.pallet && (
          <p
            className="mt-1 ml-6 text-[11px] leading-snug text-text-secondary italic"
            data-testid={`pallet-rule-${product.id}`}
          >
            {rules.pallet}
          </p>
        )}
      </div>
    </div>
  );
};

const SupportingEnableToggle = ({ product, enabled, hasFlavours, onToggleEnabled, lockNewSelections }) => {
  const noun = variantNoun(product);
  const disableAdd = !!lockNewSelections && !enabled;
  return (
    <div className="mt-3 pt-3 border-t border-border-color" onClick={(e) => e.stopPropagation()}>
      <label className={`flex items-center gap-2 text-xs text-text-primary ${disableAdd ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
        <input
          type="checkbox"
          checked={!!enabled}
          disabled={disableAdd}
          onChange={() => onToggleEnabled(product)}
          data-testid={`enable-product-${product.id}`}
          className="w-4 h-4 accent-yellow-500"
        />
        <span>Include this product</span>
        <span className="text-text-secondary">{hasFlavours ? `— pick ${noun.singular}(s)` : ''}</span>
      </label>
    </div>
  );
};

const QtyButton = ({ onClick, disabled, label, testId, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    title={disabled ? title : undefined}
    aria-label={disabled && title ? title : undefined}
    className="w-6 h-6 rounded border border-border-color text-text-primary hover:border-gold disabled:opacity-30 disabled:cursor-not-allowed"
  >
    {label}
  </button>
);

const FlavourQtyRow = ({ product, flavour, qty, onAdjustQty, onSetQty, plusDisabled, plusDisabledReason, minusTestId, plusTestId }) => (
  <div className="flex items-center justify-between gap-2 px-2 py-1 bg-black/40 border border-border-color rounded-lg">
    <span className="text-xs text-text-primary">{flavour}</span>
    <div className="flex items-center gap-1">
      <QtyButton onClick={() => onAdjustQty(product, flavour, -1)} disabled={qty === 0} label="−" testId={minusTestId} />
      {onSetQty ? (
        <input
          type="number"
          min="0"
          value={qty}
          onChange={(e) => onSetQty(product, flavour, e.target.value)}
          onClick={(e) => e.stopPropagation()}
          data-testid={`qty-input-${product.id}-${flavour}`}
          className="w-12 bg-white border border-gray-400 rounded text-center text-xs font-bold text-black py-0.5 focus:border-gold focus:ring-1 focus:ring-gold focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
      ) : (
        <span className="w-6 text-center text-xs font-semibold text-gold">{qty}</span>
      )}
      <QtyButton onClick={() => onAdjustQty(product, flavour, +1)} disabled={plusDisabled} label="+" testId={plusTestId} title={plusDisabledReason} />
    </div>
  </div>
);

const FoundationBoxFlavours = ({ product, boxSize, tier, totalBoxQty, getFlavourQty, onAdjustQty, onSetQty }) => {
  // Different caps per tier:
  //   single — 7 tubs (1kg) or 5 tubs (2kg)
  //   box    — at least 1 full box, partials on top, max 5 boxes − 1 tub
  //   pallet — unlimited (5+ boxes, no upper bound)
  let stepperCap;
  let counterLabel;
  let counterStatus;
  if (tier === 'pallet') {
    stepperCap = 9999;
    counterLabel = `Flavours (≥${5 * boxSize} tubs · ${totalBoxQty} selected)`;
    counterStatus = `${totalBoxQty}`;
  } else if (tier === 'single') {
    // Single Tub tier: 1 to 7 tubs (small) / 1 to 5 tubs (large).
    stepperCap = boxSize === 8 ? 7 : 5;
    counterLabel = `Flavours (1 to ${stepperCap} tubs)`;
    counterStatus = `${totalBoxQty} / ${stepperCap}`;
  } else {
    // Box tier: min 1 full box (boxSize tubs), partials welcome, up to 5 boxes − 1.
    stepperCap = 5 * boxSize - 1;
    counterLabel = `Flavours (min ${boxSize} tubs = 1 box, max ${stepperCap})`;
    counterStatus = `${totalBoxQty} / ${stepperCap}`;
  }
  let tone;
  if (tier === 'pallet') tone = 'text-gold';
  else if (tier === 'box') {
    if (totalBoxQty > stepperCap) tone = 'text-danger';
    else tone = totalBoxQty >= boxSize ? 'text-success' : 'text-warning';
  } else tone = flavourCounterTone(totalBoxQty, stepperCap);
  return (
    <div className="mt-3 pt-3 border-t border-border-color" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs text-text-secondary">{counterLabel}</label>
        <span className={`text-xs font-semibold ${tone}`}>{counterStatus}</span>
      </div>
      <div className="space-y-2">
        {sortFlavours(product.flavours).map((f) => (
          <FlavourQtyRow
            key={f}
            product={product}
            flavour={f}
            qty={getFlavourQty(product.id, f)}
            onAdjustQty={onAdjustQty}
            onSetQty={onSetQty && ((p, fl, v) => {
              // Clamp typed values to what the tier cap leaves for this flavour.
              const remaining = stepperCap - (totalBoxQty - getFlavourQty(p.id, fl));
              onSetQty(p, fl, Math.min(Math.max(0, Math.floor(Number(v) || 0)), remaining));
            })}
            plusDisabled={totalBoxQty >= stepperCap}
            minusTestId={`box-qty-minus-${product.id}-${f}`}
            plusTestId={`box-qty-plus-${product.id}-${f}`}
          />
        ))}
      </div>
    </div>
  );
};

const SupportingQtyFlavours = ({ product, getFlavourQty, onAdjustQty, onSetQty, plusDisabled, plusDisabledReason, skuCap }) => {
  const noun = variantNoun(product);
  return (
    <div className="mt-3 pt-3 border-t border-border-color" onClick={(e) => e.stopPropagation()}>
      <label className="text-xs text-text-secondary mb-2 block">{noun.label} (any quantity)</label>
      <div className="space-y-2">
        {sortFlavours(product.flavours).map((f) => {
          const qty = getFlavourQty(product.id, f);
          const skuFull = skuCap != null && qty >= skuCap;
          return (
            <FlavourQtyRow
              key={f}
              product={product}
              flavour={f}
              qty={qty}
              onAdjustQty={onAdjustQty}
              onSetQty={onSetQty}
              plusDisabled={plusDisabled || skuFull}
              plusDisabledReason={skuFull ? `Max ${skuCap} of this flavour (one per 2 kg tub)` : plusDisabledReason}
              minusTestId={`qty-minus-${product.id}-${f}`}
              plusTestId={`qty-plus-${product.id}-${f}`}
            />
          );
        })}
      </div>
    </div>
  );
};

const SupportingQtySingle = ({ product, productQty, onAdjustQty, onSetQty, plusDisabled, plusDisabledReason, skuCap }) => {
  const skuFull = skuCap != null && productQty >= skuCap;
  return (
    <div className="mt-3 pt-3 border-t border-border-color" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between gap-2 px-2 py-1 bg-black/40 border border-border-color rounded-lg">
        <span className="text-xs text-text-primary">Quantity</span>
        <div className="flex items-center gap-1">
          <QtyButton onClick={() => onAdjustQty(product, null, -1)} disabled={productQty === 0} label="−" testId={`qty-minus-${product.id}`} />
          {onSetQty ? (
            <input
              type="number"
              min="0"
              value={productQty}
              onChange={(e) => onSetQty(product, null, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              data-testid={`qty-input-${product.id}`}
              className="w-12 bg-white border border-gray-400 rounded text-center text-xs font-bold text-black py-0.5 focus:border-gold focus:ring-1 focus:ring-gold focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          ) : (
            <span className="w-6 text-center text-xs font-semibold text-gold">{productQty}</span>
          )}
          <QtyButton
            onClick={() => onAdjustQty(product, null, +1)}
            disabled={!!plusDisabled || skuFull}
            label="+"
            testId={`qty-plus-${product.id}`}
            title={skuFull ? `Max ${skuCap} of this product (one per 2 kg tub)` : plusDisabledReason}
          />
        </div>
      </div>
    </div>
  );
};

const FlavourCheckboxGrid = ({ product, selectedFlavours, onToggleFlavour, addDisabled, isFoundationSingle }) => {
  const noun = variantNoun(product);
  const helper = isFoundationSingle
    ? `${noun.label} (select one)`
    : `${noun.label} (select one or more)`;
  return (
    <div className="mt-3 pt-3 border-t border-border-color" onClick={(e) => e.stopPropagation()}>
      <label className="text-xs text-text-secondary mb-2 block">{helper}</label>
      <div className="grid grid-cols-2 gap-2">
        {sortFlavours(product.flavours).map((f) => {
          const checked = !!selectedFlavours?.includes(f);
          const disabled = !checked && !!addDisabled;
          return (
            <label
              key={f}
              data-testid={`flavour-option-${product.id}-${f}`}
              className={`flex items-center gap-2 px-2 py-1.5 text-xs rounded-lg border transition-colors ${
                checked
                  ? 'bg-gold/10 border-gold text-gold cursor-pointer'
                  : disabled
                    ? 'border-border-color/40 text-text-secondary/50 opacity-50 cursor-not-allowed'
                    : 'border-border-color text-text-secondary hover:border-gold/50 cursor-pointer'
              }`}
              title={disabled ? 'Per-range cap reached' : undefined}
            >
              <input
                type={isFoundationSingle ? 'radio' : 'checkbox'}
                name={isFoundationSingle ? `foundation-single-${product.id}` : undefined}
                checked={checked}
                disabled={disabled}
                onChange={() => onToggleFlavour(product, f)}
                className="w-3.5 h-3.5 accent-yellow-500"
              />
              <span>{f}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export const ProductCard = ({
  product,
  isSelected,
  selectedFlavours,
  onSelect,
  onToggleFlavour,
  multiFlavourMode,
  boxMode,
  boxSize,
  getFlavourQty,
  getProductQty,
  onAdjustQty,
  onSetQty,
  skuCap = null,
  onToggleBoxMode,
  quantityMode,
  singleMode,
  onToggleSingleMode,
  palletMode,
  onTogglePalletMode,
  enabled,
  onToggleEnabled,
  plusDisabled,
  plusDisabledReason,
  lockNewSelections,
  showSingleTier = true,
  productDiscounts = {},
  showCalc = false,
  dimmed = false,
}) => {
  const hasFlavours = product.flavours && product.flavours.length > 0;
  const selectedCount = selectedFlavours?.length || 0;
  // Core "quantity" mode renders per-flavour steppers for Single + Box + Pallet
  // tiers. Each tier has its own cap (see FoundationBoxFlavours).
  const coreQtyMode = boxMode || palletMode || (singleMode && hasFlavours);
  const coreTier = palletMode ? 'pallet' : (boxMode ? 'box' : 'single');
  const totalBoxQty = coreQtyMode ? totalFoundationBoxQty(product, getFlavourQty) : 0;
  const productQty = getProductQty ? getProductQty(product.id) : 0;
  const useQty = coreQtyMode || quantityMode;

  // Visual selection state — extracted from nested ternary for readability.
  let visuallySelected;
  if (useQty) {
    visuallySelected = coreQtyMode ? totalBoxQty > 0 : productQty > 0;
  } else if (!hasFlavours && !boxSize) {
    // No-flavour supporting card: selected when qty > 0 (single-mode stepper).
    visuallySelected = productQty > 0;
  } else if (multiFlavourMode) {
    visuallySelected = selectedCount > 0;
  } else {
    visuallySelected = isSelected;
  }

  const cardClickHandler = (e) => {
    if (multiFlavourMode || coreQtyMode || quantityMode) return;
    if (e.target.closest('select') || e.target.closest('input') || e.target.closest('label') || e.target.closest('button')) return;
    onSelect(product);
  };

  const qtyBadge = cardQtyBadge({
    boxMode: coreQtyMode, totalBoxQty, quantityMode, productQty, hasFlavours, multiFlavourMode, selectedCount, product, getFlavourQty,
  });

  // Decide which detail section to render under the header (kept linear, no nested ternaries).
  const renderDetailSection = () => {
    if (coreQtyMode && hasFlavours) {
      return (
        <FoundationBoxFlavours
          product={product}
          boxSize={boxSize}
          tier={coreTier}
          totalBoxQty={totalBoxQty}
          getFlavourQty={getFlavourQty}
          onAdjustQty={onAdjustQty}
          onSetQty={onSetQty}
        />
      );
    }
    if (!coreQtyMode && quantityMode && hasFlavours && enabled) {
      return <SupportingQtyFlavours product={product} getFlavourQty={getFlavourQty} onAdjustQty={onAdjustQty} onSetQty={onSetQty} plusDisabled={plusDisabled} plusDisabledReason={plusDisabledReason} skuCap={skuCap} />;
    }
    // No-flavour supporting card: stepper shown whenever enabled (covers both single & box modes).
    if (!coreQtyMode && !hasFlavours && enabled && !boxSize) {
      return <SupportingQtySingle product={product} productQty={productQty} onAdjustQty={onAdjustQty} onSetQty={onSetQty} plusDisabled={plusDisabled} plusDisabledReason={plusDisabledReason} skuCap={skuCap} />;
    }
    if (!coreQtyMode && quantityMode && !hasFlavours && enabled) {
      return <SupportingQtySingle product={product} productQty={productQty} onAdjustQty={onAdjustQty} onSetQty={onSetQty} plusDisabled={plusDisabled} plusDisabledReason={plusDisabledReason} skuCap={skuCap} />;
    }
    const showCheckboxes = !useQty && multiFlavourMode && hasFlavours
      && ((!boxSize && enabled) || (boxSize && singleMode));
    if (showCheckboxes) {
      const isFoundationSingle = !!boxSize && !!singleMode;
      // Foundation Single is a RADIO group — ticking a different flavour REPLACES the current
      // Core selection (handleToggleFlavour drops prior Core entries first). It must keep
      // working even when the bundle is otherwise satisfied or any range cap is reached.
      return (
        <FlavourCheckboxGrid
          product={product}
          selectedFlavours={selectedFlavours}
          onToggleFlavour={onToggleFlavour}
          addDisabled={isFoundationSingle ? false : plusDisabled}
          isFoundationSingle={isFoundationSingle}
        />
      );
    }
    return null;
  };

  return (
    <div
      data-testid={`product-card-${product.id}`}
      aria-disabled={dimmed || undefined}
      className={`product-card p-4 rounded-xl border ${
        visuallySelected ? 'selected border-gold' : 'border-border-color bg-surface hover:bg-surface-hover'
      } ${(multiFlavourMode || coreQtyMode || quantityMode) ? '' : 'cursor-pointer'} ${
        dimmed ? 'opacity-40 grayscale pointer-events-none select-none' : ''
      }`}
      onClick={cardClickHandler}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="font-semibold text-text-primary mb-1">{product.name}</h3>
          {product.size && (
            <p className="text-xs text-gold mb-2">
              {product.size}{product.sizeLabel ? ` ${product.sizeLabel}` : ''}
            </p>
          )}
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-xs text-text-secondary" data-testid={`product-price-row-${product.id}`}>
            <span>RRP: ${product.rrp.toFixed(2)}</span>
            <span
              className="text-success font-semibold"
              data-testid={`product-your-price-${product.id}`}
              title={showCalc ? priceCalcDescription(product, productDiscounts).formula : undefined}
            >
              W/sale price: ${customerUnitPrice(product, productDiscounts).toFixed(2)} + gst
            </span>
            {showCalc && (
              <span
                className="inline-flex items-center text-gold/70 hover:text-gold cursor-help"
                data-testid={`product-price-calc-${product.id}`}
                title={priceCalcDescription(product, productDiscounts).formula}
              >
                <Info size={13} weight="bold" />
              </span>
            )}
          </div>
        </div>
        {visuallySelected && (
          <div className="ml-2 flex flex-col items-end gap-1">
            <CheckCircle size={24} weight="fill" className="text-gold" />
            {qtyBadge !== null && <span className="text-xs text-gold font-semibold">{qtyBadge}×</span>}
          </div>
        )}
      </div>

      {/* Foundation cards: dual-mode selector. Supporting cards: enable toggle. */}
      {boxSize
        ? <FoundationModeToggles product={product} boxSize={boxSize} singleMode={singleMode} boxMode={boxMode} palletMode={palletMode} onToggleSingleMode={onToggleSingleMode} onToggleBoxMode={onToggleBoxMode} onTogglePalletMode={onTogglePalletMode} showSingleTier={showSingleTier} />
        : <SupportingEnableToggle product={product} enabled={enabled} hasFlavours={hasFlavours} onToggleEnabled={onToggleEnabled} lockNewSelections={lockNewSelections} />
      }

      {renderDetailSection()}
    </div>
  );
};

export default ProductCard;
